interface AIExperienceCategory {
  title: string;
  icon: string;
  description: string;
  items: string[];
  colorClass: string;
  bgColorClass: string;
}

function AIExperiences(): JSX.Element {
  const aiCategories: AIExperienceCategory[] = [
    {
      title: "Claude Code Mastery",
      icon: "🤖",
      description: "Advanced daily usage of Claude Code for full-stack development workflows",
      items: [
        "Custom CLAUDE.md with behavioral rules, domain context, and coding conventions",
        "Slash commands for repeatable workflows like database design and API integration",
        "Figma screenshot export tools providing visual context for AI-assisted development",
        "Automated E2E test generation with backward compatibility testing",
        "Database migration generation with seed data and schema evolution"
      ],
      colorClass: "text-purple-800",
      bgColorClass: "bg-purple-50"
    },
    {
      title: "MCP Server Integration",
      icon: "🔌",
      description: "Model Context Protocol servers for extending AI capabilities",
      items: [
        "Context7 MCP for real-time library documentation retrieval",
        "Dart/Flutter MCP for hot reload, widget inspection, and package search",
        "Playwright MCP for browser automation and accessibility testing",
        "Mobile MCP for iOS/Android device automation and testing",
        "Figma API integration for design-to-context workflows and screenshot export"
      ],
      colorClass: "text-orange-800",
      bgColorClass: "bg-orange-50"
    },
    {
      title: "RAG & Embeddings",
      icon: "🔍",
      description: "Retrieval-Augmented Generation and semantic search systems",
      items: [
        "Vector database integration with pgvector for similarity search",
        "OpenAI embeddings for semantic content matching and retrieval",
        "Multi-language content retrieval with translation overlay patterns",
        "Confluence and Jira content crawling with Puppeteer for knowledge base indexing"
      ],
      colorClass: "text-teal-800",
      bgColorClass: "bg-teal-50"
    },
    {
      title: "LLM Integrations",
      icon: "🔗",
      description: "Building products and features with LLM APIs",
      items: [
        "OpenAI GPT-4 and embeddings API for content analysis and generation",
        "Anthropic Claude API for code generation and review workflows",
        "Structured prompt engineering with JSON response schemas",
        "Production AI features with rate limiting and cost optimization"
      ],
      colorClass: "text-blue-800",
      bgColorClass: "bg-blue-50"
    },
    {
      title: "Custom AI Agents",
      icon: "🧠",
      description: "Building multi-agent systems and AI orchestration",
      items: [
        "Multi-agent frameworks for collaborative software development",
        "Autonomous coding workflows with role-based agent coordination",
        "Domain-Driven Design principles applied to AI agent architecture",
        "Automated scaffolding systems for database, API, and test generation"
      ],
      colorClass: "text-green-800",
      bgColorClass: "bg-green-50"
    },
    {
      title: "AI Team Enablement",
      icon: "👥",
      description: "Enabling teams to leverage AI tools effectively",
      items: [
        "MCP server configuration and custom tool development training",
        "CLAUDE.md best practices for project-specific AI instructions",
        "Prompt engineering patterns for consistent code generation",
        "Token usage optimization and model selection strategies"
      ],
      colorClass: "text-indigo-800",
      bgColorClass: "bg-indigo-50"
    }
  ];

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">AI Experiences</h2>
      <p className="text-gray-600 mb-6">Hands-on experience with modern AI tools and development practices</p>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {aiCategories.map((category: AIExperienceCategory, index: number) => (
          <div
            key={index}
            className={`${category.bgColorClass} rounded-lg p-5 border border-gray-200 hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">{category.icon}</span>
              <h3 className={`text-lg font-semibold ${category.colorClass}`}>
                {category.title}
              </h3>
            </div>

            <p className="text-gray-600 text-sm mb-4">
              {category.description}
            </p>

            <ul className="space-y-2">
              {category.items.map((item: string, itemIndex: number) => (
                <li
                  key={itemIndex}
                  className="flex items-start gap-2 text-sm text-gray-700"
                >
                  <span className={`${category.colorClass} mt-1`}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AIExperiences;
